<script lang="ts">
  import { selectors, gameEngine } from '@engine/index';
  import { FAST_MAX_AHEAD } from '@protocol/messages';
  import { app, bindActive, getActive, leaveGame, resetGameUiState, savePerGame } from '../state.svelte';
  import { restartSoloGame } from '../net';
  import { governColonies } from '../governor';
  import { autoExploreScouts, reconcilePins } from '../quickBuild';
  import { playerColor, syncEmpireColors } from '../colors';
  import { BRAND, FULL_TITLE } from '../brand';
  import { latchEdge, type EdgeLatch, type EdgeLevel } from '../commitEdge';
  import { describeSaveError, downloadRawDatabase, downloadSave } from '../saveload';
  import Spreadsheet from './Spreadsheet.svelte';
  import MapView from './MapView.svelte';
  import Research from './Research.svelte';
  import Fleets from './Fleets.svelte';
  import Designer from './Designer.svelte';
  import Empires from './Empires.svelte';
  import Reports from './Reports.svelte';
  import BattleOrdersDialog from '../battle/BattleOrdersDialog.svelte';
  import BattleViewer from '../battle/BattleViewer.svelte';
  import GroundBattleDialog from '../battle/GroundBattleDialog.svelte';
  import TimelapseViewer from '../components/TimelapseViewer.svelte';
  import StarScanDialog from '../components/StarScanDialog.svelte';
  import DiplomatPortrait from '../components/DiplomatPortrait.svelte';
  import FirstContactDialog from '../components/FirstContactDialog.svelte';
  import { musicCue, musicEnabled, toggleMusic } from '../music';
  import { generateTimelapse, type TimelapseData } from '../timelapse';

  let tab = $state<'colonies' | 'map' | 'research' | 'fleets' | 'designer' | 'empires' | 'reports'>('colonies');
  let seenReports = $state(0);
  let musicOn = $state(musicEnabled());
  let chatText = $state('');
  let chatTo = $state(-1); // -1 = everyone, else a playerId (DM)
  let showHelp = $state(false);

  const session = () => getActive()!.session;
  const gs = $derived.by(() => {
    void app.version;
    return session().getPlanned();
  });
  // one consistent color per player on every surface: fold chosen banner
  // colors into the shared registry whenever the state changes
  $effect(() => {
    if (gs) syncEmpireColors(gs.empires);
  });
  const summary = $derived.by(() => {
    void app.version;
    const s = session().getPlanned();
    return s ? selectors.empireSummary(s, session().playerId) : null;
  });
  // bugs.md: glanceable enemy-ship count — 0 means scanners see no threats,
  // so players don't have to sweep the map to know they are safe
  const enemyDetected = $derived(gs ? selectors.detectedEnemyShips(gs, session().playerId) : 0);
  // the whole per-turn cash ledger on hover: every line the pipeline will
  // actually charge or credit, summing exactly to the displayed delta — so
  // "+8 shown but +1 real" can never happen again (leader salaries used to
  // be charged invisibly)
  const bcTip = $derived.by(() => {
    if (!summary) return '';
    const b = summary.bcBreakdown;
    const sign = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
    const lines = [
      `treasury ${summary.bc} BC, ${sign(summary.bcDelta)}/turn:`,
      `  ${sign(b.colonyIncome)} colony income (taxes, specials, trade, empire tax − building upkeep)`,
    ];
    if (b.tradeSurplusBC > 0) lines.push(`  +${b.tradeSurplusBC} surplus food sold (fantastic traders)`);
    if (b.megawealth > 0) lines.push(`  +${b.megawealth} megawealth officers`);
    if (b.freighterUpkeep > 0) lines.push(`  −${b.freighterUpkeep} freighter upkeep (in use this turn)`);
    if (b.leaderSalaries > 0) lines.push(`  −${b.leaderSalaries} leader salaries`);
    if (b.cpOverage > 0) lines.push(`  −${b.cpOverage} command points over support (10 BC each)`);
    if (lines.length === 2) lines.push('  no upkeep charges this turn');
    return lines.join('\n');
  });
  // map-view quick builds: when a new turn opens, drop pins that completed
  // (or were manually removed) so their yards return to autopilot
  let reconciledTurn = -1;
  $effect(() => {
    const s = gs;
    if (!s || s.turn === reconciledTurn) return;
    reconciledTurn = s.turn;
    if (reconcilePins(s, session().playerId, app.pins)) savePerGame();
  });
  // slider autopilot: run the governor once per (turn, weights, pins) — a
  // repeat pass with identical inputs would only echo the same commands
  // forever, but a cancelled pin must hand its yard back the same turn
  let governedFp = '';
  $effect(() => {
    const s = gs;
    if (!s || !app.autopilot.enabled || s.phase !== 'planning' || s.winner !== null) return;
    const pinnedIds = Object.keys(app.pins).filter((k) => (app.pins[Number(k)]?.length ?? 0) > 0);
    const fp = `${s.turn}:${JSON.stringify(app.autopilot.weights)}:${pinnedIds.join(',')}`;
    if (fp === governedFp) return;
    governedFp = fp;
    governColonies(session(), app.autopilot.weights, new Set(pinnedIds.map(Number)));
  });
  // auto-explore: idle scouts chart the nearest unexplored star in range —
  // ordinary move_ships orders, one pass per turn, re-routable like any move
  let exploredFp = -1;
  $effect(() => {
    const s = gs;
    if (!s || !app.autoExplore || s.phase !== 'planning' || s.winner !== null || s.turn === exploredFp) return;
    exploredFp = s.turn;
    autoExploreScouts(session());
  });
  // research queue: the moment the labs go idle, start the first queued field
  // that is currently offered (completed entries fall off; not-yet-unlocked
  // deeper fields stay queued and wait their turn)
  let researchFp = '';
  $effect(() => {
    const s = gs;
    if (!s || s.phase !== 'planning' || s.winner !== null || !app.researchQueue.length) return;
    const emp = s.empires.find((e) => e.id === session().playerId);
    if (!emp || emp.research.fieldNum !== null) return;
    const fp = `${s.turn}:${app.researchQueue.map((q) => q.fieldNum).join(',')}`;
    if (fp === researchFp) return;
    researchFp = fp;
    startQueuedResearch();
  });
  function startQueuedResearch() {
    const s = session().getPlanned();
    if (!s) return;
    const me = session().playerId;
    const emp = s.empires.find((e) => e.id === me);
    if (!emp) return;
    const choices = selectors.researchChoices(s, me);
    const done = new Set(emp.completedFields);
    // completed entries drop out; then start the first offered one
    const queue = app.researchQueue.filter((q) => !done.has(q.fieldNum));
    app.researchQueue = queue;
    for (let i = 0; i < queue.length; i++) {
      const entry = queue[i]!;
      const choice = choices.find((c) => c.field.num === entry.fieldNum);
      if (!choice) continue; // deeper field, not unlocked yet — keep waiting
      const target = choice.grantsAll
        ? null
        : (choice.apps.find((a) => a.id === entry.targetApp && !a.known && !a.dead) ??
            choice.apps.find((a) => !a.known && !a.dead) ??
            choice.apps.find((a) => !a.known))?.id ?? null;
      const res = session().submit('set_research', { fieldNum: entry.fieldNum, targetApp: target });
      app.researchQueue = queue.filter((_, j) => j !== i); // started (or rejected: drop, don't loop)
      savePerGame();
      if (res.error) app.rejectedNote = `⛔ research queue: ${res.error}`;
      return;
    }
    savePerGame();
  }
  const committed = $derived.by(() => {
    void app.version;
    return session().getCommitted();
  });
  const roster = $derived.by(() => {
    void app.version;
    return session().getRoster();
  });
  const iCommitted = $derived(committed.includes(session().playerId));
  /** who this connection plays (a resumed save matches seats by name) */
  const mySeatName = $derived.by(() => {
    void app.version;
    const me = session().playerId;
    const player = roster.find((p) => p.id === me)?.name ?? `#${me}`;
    const race = gs?.empires.find((e) => e.id === me)?.raceName;
    return race && race !== player ? `${player} · ${race}` : player;
  });
  const botOnSeat = (seatId: number) => getActive()?.bots.find((b) => b.seatId === seatId) ?? null;
  // snapshot the fields: pbm.note mutates in place on a non-reactive object,
  // so returning the object itself would never re-render the banner
  const pbmInfo = $derived.by(() => {
    void app.version;
    const p = getActive()?.pbm;
    return p ? { role: p.role, note: p.note } : null;
  });
  /** host view: seats a bot could take over, or is holding */
  const seatIssues = $derived.by(() => {
    void app.version;
    if (!getActive()?.host) return [];
    return roster
      .map((p) => ({ p, bot: botOnSeat(p.id) }))
      .filter(({ p, bot }) => p.id !== session().playerId && (!p.connected || bot));
  });
  const winner = $derived(gs?.winner ?? null);
  const authHash = $derived.by(() => {
    void app.version;
    const auth = session().getState();
    return auth ? gameEngine.hash(auth) : '';
  });
  /** the battle this player must currently order (dialog), if any */
  const myBattle = $derived.by(() => {
    void app.version;
    const auth = session().getState();
    if (!auth || auth.phase !== 'battle_orders') return null;
    const me = session().playerId;
    // first battle whose OWN-side orders are still unsubmitted — battles stay
    // in pendingBattles until all resolve, so matching on participation alone
    // re-selects an already-ordered battle #1 and battle #2 never gets a dialog
    return (
      auth.pendingBattles.find(
        (b) => (b.attacker === me && b.ordersA === null) || (b.defender === me && b.ordersD === null),
      ) ??
      auth.pendingBattles.find((b) => b.attacker === me || b.defender === me) ??
      null
    );
  });
  const battlePhaseInfo = $derived.by(() => {
    void app.version;
    const auth = session().getState();
    if (!auth || auth.phase !== 'battle_orders') return null;
    const nameOf = (id: number) =>
      id === -2 ? 'Monsters' : id === -3 ? 'Andromedans' : (auth.empires.find((e) => e.id === id)?.name ?? `Empire ${id}`);
    return auth.pendingBattles
      .map((b) => `${nameOf(b.attacker)} vs ${nameOf(b.defender)} at ${auth.stars.find((s) => s.id === b.starId)?.name ?? 'an unknown star'}`)
      .join('; ');
  });
  /** labs idle: no field selected and nothing queued — RP is being banked */
  const researchIdle = $derived(summary !== null && summary.researching === null);
  // ---- fast start (async turns until contact) ----
  const fastActive = $derived.by(() => {
    void app.version;
    return session().fastPhaseActive();
  });
  const fastAhead = $derived.by(() => {
    void app.version;
    return session().fastAheadTurns();
  });
  const fastBlind = $derived.by(() => {
    void app.version;
    return session().fastBlind();
  });
  const syncedTurn = $derived.by(() => {
    void app.version;
    const auth = session().getState();
    return auth ? gameEngine.turnOf(auth) : 0;
  });
  /** slowest player's committed-through turn (fast phase status line) */
  const slowestInfo = $derived.by(() => {
    void app.version;
    if (!fastActive) return null;
    const turns = session().getFastTurns();
    const behind = roster
      .filter((p) => p.id !== session().playerId)
      .map((p) => ({ name: p.name, through: turns[String(p.id)] ?? syncedTurn - 1 }))
      .sort((x, y) => x.through - y.through);
    return behind[0] ?? null;
  });
  /** solo (vs local bots) game: the bots commit instantly, so "everyone else
   * has committed" is true every single turn — pure noise, never shown */
  const soloGame = $derived.by(() => {
    void app.version;
    return (getActive()?.soloBots.length ?? 0) > 0;
  });
  /** commit urgency: red = everyone else committed and they are waiting on you;
   * green = others have started committing. Latched one-way per turn so an
   * opponent cycling commit/uncommit cannot flash the screen. */
  const othersTotal = $derived(roster.length - 1);
  const othersCommitted = $derived(committed.filter((id) => id !== session().playerId).length);
  const commitEdge = $derived<EdgeLevel>(
    iCommitted || othersTotal <= 0 || othersCommitted === 0
      ? ''
      : othersCommitted === othersTotal
        ? 'red'
        : 'green',
  );
  let edgeLatch = $state<EdgeLatch>({ turn: 0, level: '' });
  $effect(() => {
    const next = latchEdge(edgeLatch, gs?.turn ?? 0, gs?.phase ?? 'planning', commitEdge);
    if (next !== edgeLatch) edgeLatch = next;
  });
  const edgeShown = $derived(
    fastActive || soloGame || iCommitted || (gs?.turn ?? 0) !== edgeLatch.turn ? '' : edgeLatch.level,
  );
  const memoryOnly = $derived.by(() => {
    void app.version;
    return getActive()?.memoryOnly ?? false;
  });
  let memoryNoteDismissed = $state(false);
  const autoTurnSeconds = $derived.by(() => {
    void app.version;
    return session().getSettings()?.autoTurnSeconds ?? 0;
  });
  const realtimeTurns = $derived.by(() => {
    void app.version;
    return (session().getSettings()?.realtimeTurnSeconds ?? 0) > 0;
  });
  // countdown display for the armed auto-turn timer (ticks locally)
  let nowTick = $state(Date.now());
  $effect(() => {
    const iv = setInterval(() => (nowTick = Date.now()), 1000);
    return () => clearInterval(iv);
  });
  const autoTurnRemaining = $derived.by(() => {
    void app.version;
    const deadline = session().getAutoTurnDeadline();
    if (deadline === null) return null;
    return Math.max(0, Math.ceil((deadline - nowTick) / 1000));
  });
  /** live leader offers for this player (drives the nav badge) */
  const leaderOfferCount = $derived.by(() => {
    if (!gs) return 0;
    const me = session().playerId;
    return gs.leaderOffers.filter((o) => o.empireId === me && o.expiresTurn > gs.turn).length;
  });
  /** offers not muted with 🔕 — only these stop fast-forward (bugs.md); an
   * ignored offer stays on the Empires tab and can still be hired any time */
  const urgentOfferCount = $derived.by(() => {
    if (!gs) return 0;
    const me = session().playerId;
    return gs.leaderOffers.filter(
      (o) => o.empireId === me && o.expiresTurn > gs.turn && !app.ignoredOffers.includes(`${o.leaderId}:${o.expiresTurn}`),
    ).length;
  });
  function endTurn() {
    flushTelemetry();
    session().endTurnFast();
  }

  // ---- ⏩ fast-forward: auto-play turns until something needs a DECISION.
  // With autopilot + the research queue + auto-explore on, quiet stretches
  // are pure clicking — this turns them into seconds. Every stop names its
  // reason; battles stop it via their own dialog. ----
  let ffActive = $state(false);
  let ffNote = $state('');
  let ffNoteTimer: ReturnType<typeof setTimeout> | null = null;
  let ffTimer: ReturnType<typeof setTimeout> | null = null;
  let ffActedTurn = -1;
  const emptyQueueCount = $derived.by(() => {
    if (!gs) return 0;
    const me = session().playerId;
    return gs.colonies.filter((c) => c.owner === me && !c.outpost && c.queue.length === 0).length;
  });
  function stopFF(reason: string) {
    ffActive = false;
    if (ffTimer) {
      clearTimeout(ffTimer);
      ffTimer = null;
    }
    ffNote = `⏸ auto-play stopped: ${reason}`;
    if (ffNoteTimer) clearTimeout(ffNoteTimer);
    ffNoteTimer = setTimeout(() => (ffNote = ''), 8000);
  }
  function toggleFF() {
    if (ffActive) {
      ffActive = false;
      if (ffTimer) {
        clearTimeout(ffTimer);
        ffTimer = null;
      }
      ffNote = '';
      return;
    }
    ffNote = '';
    ffActedTurn = -1;
    ffActive = true;
  }
  /** what (if anything) needs the player right now — checked at each new
   * planning turn against fresh state + the just-resolved turn's reports */
  function ffInterrupt(resolvedTurn: number): string | null {
    if (winner !== null) return 'the game is decided';
    if (app.contactFlash) return 'CONTACT';
    if (app.viewing) return 'a battle replay is up';
    if (app.viewingGround) return 'an invasion playback is up';
    if (timelapse || timelapseBusy) return 'the campaign timelapse is up';
    if (researchIdle && app.researchQueue.length === 0) return 'labs idle — pick research';
    if (urgentOfferCount > 0) return 'a leader awaits your answer';
    if ((summary?.bc ?? 0) < 0 && !app.autopilot.enabled) return 'treasury is in the red';
    if (emptyQueueCount > 0 && !app.autopilot.enabled) return 'a colony has an empty build queue';
    const fresh = app.reports.filter((r) => r.turn === resolvedTurn);
    if (fresh.some((r) => r.kind === 'colony_ship_arrived')) return 'a colony ship reached an open planet';
    if (fresh.some((r) => r.kind === 'artifact_tech')) return 'ancient artifacts yielded a technology';
    if (fresh.some((r) => r.kind === 'splinter_joined')) return 'a splinter colony joined the empire';
    return null;
  }
  $effect(() => {
    void app.version;
    const s = gs;
    if (!ffActive || !s) return;
    if (s.phase === 'battle_orders') {
      if (myBattle) stopFF('battle orders needed');
      return; // spectating someone else's battle: hold, resume after
    }
    if (s.phase !== 'planning') return;
    if (s.turn === ffActedTurn) return; // this turn's end/commit already sent
    const reason = ffInterrupt(s.turn - 1);
    if (reason) {
      stopFF(reason);
      return;
    }
    if (fastActive && (fastBlind || fastAhead >= FAST_MAX_AHEAD)) return; // capped: hold armed, resume as others catch up
    if (!fastActive && iCommitted) {
      ffActedTurn = s.turn; // commit-by-default: committed, waiting on the table
      return;
    }
    ffActedTurn = s.turn;
    // small beat between turns: lets the governor/research-queue/auto-explore
    // effects act first and keeps the map readable as it advances
    if (ffTimer) clearTimeout(ffTimer);
    ffTimer = setTimeout(() => {
      ffTimer = null;
      if (!ffActive) return;
      const now = session().getPlanned();
      if (!now || now.phase !== 'planning' || now.winner !== null) return;
      if (fastActive) endTurn();
      else if (!iCommitted) {
        flushTelemetry();
        session().commitTurn();
      }
    }, 200);
  });
  /** colonies idling in a default mode (empty queue / housing / trade goods)
   * instead of actively constructing — drives the Colonies tab badge */
  const defaultBuildCount = $derived.by(() => {
    if (!gs) return 0;
    const me = session().playerId;
    return gs.colonies.filter((c) => {
      if (c.owner !== me || c.outpost) return false;
      const head = c.queue[0]?.item ?? null;
      return head === null || head === 'housing' || head === 'trade_goods';
    }).length;
  });

  /** own fleets under way — mirrors the Colonies badge on the Fleets tab */
  const fleetsInFlight = $derived.by(() => {
    if (!gs) return 0;
    const me = session().playerId;
    return gs.ships.filter((s) => s.owner === me && s.location.kind === 'transit').length;
  });

  // ---- research breakthrough + ship/colony-ship arrival celebrations ----
  let celebration = $state<{ field: string; granted: string[] } | null>(null);
  let celebrationTimer: ReturnType<typeof setTimeout> | null = null;
  let arrival = $state<{ starId: number; planetId: number } | null>(null);
  let arrivalTimer: ReturnType<typeof setTimeout> | null = null;
  /** fleet-arrival ping: one line per destination star, this boundary only */
  let fleetArrivals = $state<Array<{ starId: number; ships: number }> | null>(null);
  let fleetArrivalTimer: ReturnType<typeof setTimeout> | null = null;
  /** first-reveal system scans, shown one at a time in charting order */
  let starScans = $state<number[]>([]);
  // ---- mid-game first meetings: the fast-start CONTACT overlay fires only
  // once, so every later first meeting gets its own envoy dialog. Contact is
  // derived, never evented — diff the met-set across state changes instead.
  let knownContacts: Set<number> | null = null; // null = not yet baselined
  let meetQueue = $state<number[]>([]);
  $effect(() => {
    void app.version;
    const s = gs;
    if (!s) {
      knownContacts = null;
      meetQueue = [];
      return;
    }
    const met = selectors.metEmpireIds(s, session().playerId);
    if (knownContacts === null) {
      // first look at this game (fresh start OR loaded save): baseline quietly
      knownContacts = new Set(met);
      return;
    }
    // the fast-start CONTACT overlay already introduces its envoys — and it
    // can be set after this effect saw the same state change, so also purge
    // its ids from an already-built queue
    const flashIds = new Set((app.contactFlash?.pairs ?? []).flat());
    if (meetQueue.some((id) => flashIds.has(id))) meetQueue = meetQueue.filter((id) => !flashIds.has(id));
    const fresh = [...met].filter((id) => id !== session().playerId && !knownContacts!.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) knownContacts.add(id);
    const toShow = fresh.filter((id) => !flashIds.has(id) && !meetQueue.includes(id));
    if (toShow.length) {
      meetQueue = [...meetQueue, ...toShow];
      musicCue('contact');
    }
  });
  // the fast-start CONTACT moment gets the same score cue
  $effect(() => {
    if (app.contactFlash) musicCue('contact');
  });
  // cursor over reportsSeq, NOT reports.length: the feed caps at 300 by
  // shifting, so length freezes there and a length-based cursor never saw
  // another report — research celebrations and arrival pings died late-game
  let processedSeq = 0;
  $effect(() => {
    const seq = app.reportsSeq;
    const reports = app.reports;
    if (seq < processedSeq) {
      processedSeq = 0; // new game
      starScans = [];
    }
    const shipsByStar = new Map<number, number>();
    const newScans: number[] = [];
    for (let i = Math.max(0, reports.length - (seq - processedSeq)); i < reports.length; i++) {
      const r = reports[i]!;
      if (r.kind === 'research_complete') {
        celebration = {
          field: String(r.payload['field'] ?? ''),
          granted: (r.payload['granted'] as string[] | undefined) ?? [],
        };
        if (celebrationTimer) clearTimeout(celebrationTimer);
        celebrationTimer = setTimeout(() => (celebration = null), 8000);
        musicCue('research');
      } else if (r.kind === 'colony_ship_arrived') {
        arrival = {
          starId: Number(r.payload['starId'] ?? 0),
          planetId: Number(r.payload['planetId'] ?? 0),
        };
        if (arrivalTimer) clearTimeout(arrivalTimer);
        arrivalTimer = setTimeout(() => (arrival = null), 12000);
        musicCue('discovery');
      } else if (r.kind === 'ship_arrived') {
        const starId = Number(r.payload['starId'] ?? 0);
        shipsByStar.set(starId, (shipsByStar.get(starId) ?? 0) + 1);
      } else if (r.kind === 'star_explored') {
        const starId = Number(r.payload['starId'] ?? 0);
        if (starId && !starScans.includes(starId) && !newScans.includes(starId)) newScans.push(starId);
      }
    }
    if (newScans.length) {
      starScans = [...starScans, ...newScans];
      // the scan dialog says it all — drop the redundant arrival toast there
      for (const sid of newScans) shipsByStar.delete(sid);
      musicCue('discovery');
    }
    if (shipsByStar.size > 0) {
      fleetArrivals = [...shipsByStar.entries()].map(([starId, ships]) => ({ starId, ships }));
      if (fleetArrivalTimer) clearTimeout(fleetArrivalTimer);
      fleetArrivalTimer = setTimeout(() => (fleetArrivals = null), 12000);
    }
    processedSeq = seq;
  });
  const pretty = (id: string) => id.replaceAll('_', ' ');
  const starName = (id: number) => gs?.stars.find((s) => s.id === id)?.name ?? `star ${id}`;

  // ---- UI telemetry: seconds + visits per screen, flushed with each commit.
  // Visits ride in the same payload under 'visits:<screen>' keys so future
  // games can tell many-quick-checks from long-stares (the turn-297 analysis
  // had only aggregate seconds, which could not distinguish the two) ----
  let dwellStart = Date.now();
  let dwell: Record<string, number> = {};
  let visits: Record<string, number> = { colonies: 1 }; // the game opens on colonies
  let dwellTab: typeof tab = 'colonies'; // matches tab's initial value
  $effect(() => {
    if (tab === dwellTab) return;
    const secs = Math.floor((Date.now() - dwellStart) / 1000);
    if (secs > 0) dwell[dwellTab] = (dwell[dwellTab] ?? 0) + Math.min(secs, 3600);
    dwellTab = tab;
    dwellStart = Date.now();
    visits[tab] = (visits[tab] ?? 0) + 1;
  });
  function flushTelemetry() {
    const secs = Math.floor((Date.now() - dwellStart) / 1000);
    if (secs > 0) dwell[dwellTab] = (dwell[dwellTab] ?? 0) + Math.min(secs, 3600);
    dwellStart = Date.now();
    const screens: Record<string, number> = Object.fromEntries(Object.entries(dwell).filter(([, v]) => v > 0));
    for (const [k, v] of Object.entries(visits)) {
      if (v > 0) screens[`visits:${k}`] = v; // sub-second visits still count
    }
    dwell = {};
    visits = {};
    if (Object.keys(screens).length) session().submit('record_telemetry', { screens });
  }

  function toggleCommit() {
    if (iCommitted) session().uncommitTurn();
    else {
      flushTelemetry();
      session().commitTurn();
    }
  }
  function sendChat() {
    if (chatText.trim()) session().sendChat(chatText.trim(), chatTo);
    chatText = '';
  }
  const chatVisible = $derived.by(() => {
    void app.version;
    const me = session().playerId;
    // "all": broadcasts plus any DMs involving me (an incoming DM must not
    // vanish just because the box is on "all"); a DM thread shows only the
    // exchange between me and that player — not every DM I ever sent
    return app.chat.filter((m) =>
      chatTo === -1
        ? m.to === -1 || m.to === me || m.from === me
        : (m.from === chatTo && m.to === me) || (m.from === me && m.to === chatTo),
    );
  });

  let saveNote = $state('');
  let saveNoteTimer: ReturnType<typeof setTimeout> | null = null;
  function flashSaveNote(text: string) {
    saveNote = text;
    if (saveNoteTimer) clearTimeout(saveNoteTimer);
    saveNoteTimer = setTimeout(() => (saveNote = ''), 6000);
  }
  let saveNoHistory = $state(false);
  let botAggressive = $state(getActive()?.solo?.isAggressive() ?? false);
  async function saveGame() {
    try {
      const name = await downloadSave(getActive()!, { history: !saveNoHistory });
      flashSaveNote(`saved ${name}`);
    } catch (e) {
      flashSaveNote(describeSaveError(e));
    }
  }
  async function saveDb() {
    try {
      await downloadRawDatabase(getActive()!);
      flashSaveNote('database downloaded');
    } catch (e) {
      flashSaveNote(describeSaveError(e));
    }
  }

  // ---- global hotkeys: 1-7 switch tabs, E ends/commits the turn. Letters
  // beyond these belong to the screens (the map claims its own build keys). ----
  const TAB_KEYS = ['colonies', 'map', 'research', 'fleets', 'designer', 'empires', 'reports'] as const;
  function onShellKey(e: KeyboardEvent) {
    const t = e.target as HTMLElement | null;
    const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
    if (typing || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
    // never steal keys from a modal (battle orders, replay, timelapse, contact, scan)
    if (myBattle || app.viewing || app.viewingGround || app.contactFlash || timelapse || starScans.length > 0 || meetQueue.length > 0 || winner !== null) return;
    if (e.key.length === 1 && e.key >= '1' && e.key <= '7') {
      e.preventDefault();
      tab = TAB_KEYS[Number(e.key) - 1]!;
      if (tab === 'reports') seenReports = app.reportsSeq;
    } else if (e.shiftKey && (e.key === 'E' || e.key === 'e')) {
      e.preventDefault();
      toggleFF();
    } else if (e.key === 'e' || e.key === 'E') {
      e.preventDefault();
      if (fastActive) endTurn();
      else toggleCommit();
    }
  }

  // ---- 🎬 campaign timelapse: consent-gated full-history replay. The
  // engine latches timelapseReadyTurn when every living empire has opted in
  // (ballot resets — an end-of-session ritual the table can repeat); after a
  // victory anyone may watch without a vote (Empires tab bumps the request).
  let timelapse = $state<TimelapseData | null>(null);
  let timelapseBusy = $state('');
  let timelapseErr = $state('');
  let seenTimelapseTurn: number | null | undefined = undefined;
  let seenTimelapseReq = 0;
  async function openTimelapse() {
    if (timelapseBusy) return;
    timelapseErr = '';
    timelapseBusy = 'replaying the campaign… 0%';
    try {
      timelapse = await generateTimelapse(getActive()!, (pct) => (timelapseBusy = `replaying the campaign… ${pct}%`));
    } catch (e) {
      timelapseErr = `🎬 ${e instanceof Error ? e.message : String(e)}`;
    } finally {
      timelapseBusy = '';
    }
  }
  $effect(() => {
    const ready = gs?.timelapseReadyTurn ?? null;
    if (seenTimelapseTurn === undefined) {
      seenTimelapseTurn = ready; // baseline: a reload must not re-pop an old showing
      return;
    }
    if (ready !== null && ready !== seenTimelapseTurn) {
      seenTimelapseTurn = ready;
      void openTimelapse();
    }
  });
  $effect(() => {
    if (app.timelapseRequest > 0 && app.timelapseRequest !== seenTimelapseReq) {
      seenTimelapseReq = app.timelapseRequest;
      void openTimelapse();
    }
  });
  const timelapseVotes = $derived(gs?.timelapseVotes ?? []);
  const livingEmpires = $derived(gs ? gs.empires.filter((e) => !e.eliminated).length : 0);

  let restarting = $state(false);
  async function restartGame() {
    const active = getActive();
    if (!active?.soloSetup || restarting) return;
    if (!confirm('Restart: abandon this campaign and set up a fresh galaxy with the same bots?')) return;
    restarting = true;
    try {
      const next = await restartSoloGame(active);
      resetGameUiState();
      bindActive(next);
    } catch (e) {
      app.error = e instanceof Error ? e.message : String(e);
      app.screen = 'home';
    } finally {
      restarting = false;
    }
  }
</script>

<svelte:window onkeydown={onShellKey} />

{#if gs && summary}
  <header>
    <!-- End turn lives at the very top-left so it is ALWAYS in the same
         place, no matter how wide the stats to its right get (bugs.md) -->
    {#if fastActive}
      <button
        data-testid="commit"
        class="commit fast"
        class:warn={researchIdle}
        disabled={fastBlind || fastAhead >= FAST_MAX_AHEAD}
        title={fastBlind
          ? 'Recovering your fast-forwarded turns after the reload — planning resumes when the log catches up.'
          : fastAhead >= FAST_MAX_AHEAD
            ? `You are ${FAST_MAX_AHEAD} turns ahead of the slowest player — the cap. Play resumes as they catch up.`
            : researchIdle
              ? 'Warning: no research selected — RP will be banked unspent'
              : 'Fast start: ends your turn immediately — nobody is waited on until CONTACT'}
        onclick={endTurn}
      >{researchIdle ? '⚠ ' : ''}End turn ⚡{fastAhead > 0 ? ` (+${fastAhead})` : ''}</button>
    {:else}
      <button
        data-testid="commit"
        class="commit"
        class:committed={iCommitted}
        class:warn={researchIdle && !iCommitted}
        title={researchIdle && !iCommitted ? 'Warning: no research selected — RP will be banked unspent' : ''}
        onclick={toggleCommit}
      >{iCommitted ? 'Committed ✓' : researchIdle ? '⚠ Commit turn' : 'Commit turn'} ({committed.length}/{roster.length})</button>
    {/if}
    <button
      class="ff"
      class:active={ffActive}
      data-testid="fast-forward"
      title={ffActive
        ? 'auto-playing — stops the moment a decision needs you (Shift+E)'
        : 'auto-play turns until something needs you: battles, idle labs, arrivals, offers… (Shift+E). Best with autopilot + a research queue.'}
      onclick={toggleFF}
    >{ffActive ? '⏸' : '⏩'}</button>
    <span class="title" title={FULL_TITLE}>{BRAND.title}</span>
    <span class="stat" data-testid="my-seat" title="the empire you play (seat #{session().playerId}) — a resumed save matches players to their empire by name">👤 {mySeatName}</span>
    <!-- fixed-width turn stat: tabular digits + the synced turn lives in the
         tooltip, so the header never jiggles as numbers change (bugs.md) -->
    <span
      class="stat turnstat"
      data-testid="turn"
      title={fastActive && fastAhead > 0
        ? `fast start: your preview runs ${fastAhead} turn${fastAhead > 1 ? 's' : ''} ahead of the synced (slowest-player) turn ${syncedTurn}`
        : 'current turn'}
    ><span class="lbl">Turn</span> {gs.turn}{#if fastActive && fastAhead > 0}<span class="dim syncmark">⚡</span>{/if}</span>
    <span class="stat" data-testid="bc" title={bcTip}>💰 {summary.bc} <span class="delta" class:neg={summary.bcDelta < 0}>({summary.bcDelta >= 0 ? '+' : ''}{summary.bcDelta})</span></span>
    <span class="stat" data-testid="food" title="empire food surplus" class:neg={summary.foodNet < 0}>🌾 {summary.foodNet >= 0 ? '+' : ''}{summary.foodNet}</span>
    <span
      class="stat"
      data-testid="freighters"
      class:neg={summary.freightersFree < summary.freightersNeeded}
      title="freighters: {summary.freightersFree} free of {summary.freighters} total ({summary.freighters - summary.freightersFree} busy). NEEDED now: {summary.freightersNeeded} for food deliveries{summary.colonistsInTransit > 0 ? ` + ${5 * summary.colonistsInTransit} hauling ${summary.colonistsInTransit} colonist${summary.colonistsInTransit > 1 ? 's' : ''} (5 per colonist)` : ''}.{summary.freightersFree < summary.freightersNeeded ? ` SHORT ${summary.freightersNeeded - summary.freightersFree} — build freighter fleets or food goes undelivered.` : ''}"
    >🚚 {summary.freightersFree}/{summary.freighters}</span>
    <span class="stat" data-testid="rp" title="research points per turn">🔬 {summary.researchPerTurn}</span>
    <span
      class="stat"
      data-testid="cp"
      class:neg={summary.cpUsage > summary.cpSources}
      title="command points: fleet upkeep {summary.cpUsage} vs support {summary.cpSources} (colonies, star bases, tech, officers). Every point over costs 10 BC per turn."
    >⚓ {summary.cpUsage}/{summary.cpSources}</span>
    <span
      class="stat"
      data-testid="enemy-detected"
      class:neg={enemyDetected > 0}
      title={enemyDetected > 0
        ? `${enemyDetected} enemy ship${enemyDetected > 1 ? 's' : ''} on your scanners — check the map`
        : 'enemy ships on your scanners: none detected — no known threats in scanner range'}
    >📡 {enemyDetected}</span>
    <label class="tax" title="empire tax: converts this % of every colony's queue production into BC (1 prod → 1 BC)">
      🏛 tax
      <select
        data-testid="tax-rate"
        value={summary.taxRatePct}
        onchange={(e) => session().submit('set_tax_rate', { pct: Number((e.target as HTMLSelectElement).value) })}
      >
        {#each [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50] as pct (pct)}
          <option value={pct}>{pct}%</option>
        {/each}
      </select>
    </label>
    <button
      class="researching"
      class:idle={researchIdle}
      data-testid="researching"
      title={researchIdle ? 'No research selected — your labs are idle! Click to choose.' : 'current research (click to view)'}
      onclick={() => (tab = 'research')}
    >
      {#if researchIdle}
        ⚠ no research!
      {:else}
        {pretty(summary.researching ?? '')}{summary.researchOddsPct > 0
          ? ` (${summary.researchOddsPct}% chance${summary.researchTurnsLeft !== null ? ` · ${summary.researchTurnsLeft}t` : ''})`
          : summary.researchProgressPct !== null
            ? ` (${summary.researchProgressPct}% of base${summary.researchTurnsLeft !== null ? ` · ${summary.researchTurnsLeft}t` : ''})`
            : summary.researchTurnsLeft !== null
              ? ` (${summary.researchTurnsLeft}t)`
              : ''}
      {/if}
    </button>
    {#if getActive()?.solo}
      <label class="aggro" title="aggressive: the bot declares war and throws half its fleet at your nearest systems">
        <input
          type="checkbox"
          data-testid="bot-aggressive"
          checked={botAggressive}
          onchange={(e) => {
            for (const bot of getActive()?.soloBots ?? []) bot.setAggressive((e.target as HTMLInputElement).checked);
            botAggressive = (e.target as HTMLInputElement).checked;
          }}
        />
        🗡 aggressive bot
      </label>
    {/if}
    <span class="saves">
      <button
        data-testid="music-toggle-shell"
        title={musicOn ? 'music is on — click to silence' : 'music: a generative space score, synthesized in the browser'}
        onclick={() => (musicOn = toggleMusic())}
      >{musicOn ? '🎵' : '🔇'}</button>
      <button data-testid="save-game" disabled={!getActive()?.store} onclick={saveGame}
        title={getActive()?.store ? 'Download the full game as a save file (works in any tab)' : 'persistence unavailable'}>
        💾 Save
      </button>
      <label class="dim nohist" title="strip the turn-by-turn history: final state only, smaller file, no what-if branching">
        <input type="checkbox" data-testid="save-no-history" bind:checked={saveNoHistory} /> no history
      </label>
      {#if session().playerId === 0}
        <button data-testid="save-db" disabled={!getActive()?.sqlocal} onclick={saveDb}
          title="Download the raw sqlite database">DB</button>
      {/if}
      {#if saveNote}<span class="dim" data-testid="save-note">{saveNote}</span>{/if}
      {#if app.rejectedNote}<span class="dim" data-testid="rejected-note">{app.rejectedNote}</span>{/if}
      {#if getActive()?.soloSetup}
        <button data-testid="restart-game" disabled={restarting} onclick={restartGame}
          title="abandon this campaign and start a fresh galaxy against the same bots">
          {restarting ? '…' : '🔄 Restart'}
        </button>
      {/if}
      <button data-testid="leave-room" onclick={leaveGame}
        title={pbmInfo ? 'mail in: uploads your progress and hands the room to the next player' : 'leave this room (the game stays saved; rejoin any time)'}>
        {pbmInfo ? '📬 mail in & leave' : '⏏ leave'}
      </button>
    </span>
  </header>
  {#if edgeShown}
    <div class="edge {edgeShown}" aria-hidden="true"></div>
  {/if}
  <!-- "the galaxy waits on you" lives ON the chat footer now (bugs.md): a
       translucent click-through wash instead of a banner line, so it never
       reflows the layout — and never shows in solo games, where the bots
       have always already committed -->
  {#if !app.hostConnected}
    <div class="banner warn" data-testid="host-offline">
      ⚠ Host offline — the game is paused. It resumes when the host returns (or load their save file to re-host).
    </div>
  {/if}
  {#if pbmInfo}
    <div class="banner dim" data-testid="pbm-banner">
      📬 play-by-mail — {pbmInfo.note}
    </div>
  {/if}
  <!-- seat problems live on the Empires tab now (bugs.md: the big yellow
       banner must not block the whole main screen); the tab badge points there -->
  {#if timelapseBusy}
    <div class="banner dim" data-testid="timelapse-busy">🎬 {timelapseBusy}</div>
  {:else if timelapseErr}
    <div class="banner warn" data-testid="timelapse-error">{timelapseErr}</div>
  {/if}
  {#if timelapseVotes.length > 0 && winner === null}
    <div class="banner dim" data-testid="timelapse-ballot">
      🎬 Campaign-timelapse ballot: {timelapseVotes.length}/{livingEmpires} opted in — it replays the WHOLE game on an
      unfogged map, so everyone still playing must agree.
      {#if !timelapseVotes.includes(session().playerId)}
        <button data-testid="timelapse-vote" onclick={() => session().submit('timelapse_vote', {})}>🎬 opt in</button>
      {:else}
        (you opted in — waiting for the others)
      {/if}
    </div>
  {/if}
  {#if ffActive}
    <div class="banner dim" data-testid="ff-banner">
      ⏩ Auto-playing — the turn ends itself until something needs a decision (battle, idle labs, arrival, offer…). Shift+E or ⏸ to stop.
    </div>
  {:else if ffNote}
    <div class="banner warn" data-testid="ff-note">{ffNote}</div>
  {/if}
  {#if realtimeTurns && autoTurnRemaining !== null}
    <div class="banner {autoTurnRemaining <= 5 && !iCommitted ? 'warn' : 'dim'}" data-testid="auto-turn-banner">
      ⏱ Realtime: turn advances in {autoTurnRemaining}s{iCommitted ? '' : ' — commit your orders'}.
    </div>
  {:else if autoTurnRemaining !== null && !iCommitted}
    <div class="banner warn" data-testid="auto-turn-banner">
      ⏱ Everyone else has committed — the turn advances in {autoTurnRemaining}s unless you commit (or someone uncommits).
    </div>
  {:else if autoTurnRemaining !== null}
    <div class="banner dim" data-testid="auto-turn-banner">
      ⏱ Auto-turn armed: advancing in {autoTurnRemaining}s.
    </div>
  {:else if autoTurnSeconds > 0}
    <!-- timer not armed: nothing to show -->
  {/if}
  {#if fastActive && fastBlind}
    <div class="banner warn" data-testid="fast-blind-banner">
      ⚡ Recovering your fast-forwarded turns (the reload dropped the local preview) — synced turn {syncedTurn}, your orders replay as the others catch up.
    </div>
  {:else if fastActive && fastAhead >= FAST_MAX_AHEAD}
    <div class="banner warn" data-testid="fast-cap-banner">
      ⚡ You are {FAST_MAX_AHEAD} turns ahead of the slowest player{slowestInfo ? ` (${slowestInfo.name} finished turn ${Math.max(0, slowestInfo.through)})` : ''} — the cap. On CONTACT the game rewinds everyone to the synced turn.
    </div>
  {:else if fastActive && fastAhead >= FAST_MAX_AHEAD - 2}
    <div class="banner dim" data-testid="fast-ahead-banner">
      ⚡ Fast start: you are {fastAhead} turns ahead of the slowest player{slowestInfo ? ` (${slowestInfo.name})` : ''}. When empires meet, CONTACT rewinds everyone to the synced turn — progress past it is replayed from your submitted orders.
    </div>
  {/if}
  {#if winner !== null}
    {@const winLabel = gs.winType === 'council' ? 'is elected supreme ruler of the council' : gs.winType === 'antaran' ? 'has conquered the Andromedan home' : 'wins by conquest'}
    <div class="banner" data-testid="victory">
      Victory: {roster.find((p) => p.id === winner)?.name ?? winner} {winLabel}!
      <button data-testid="timelapse-watch-victory" title="replay the whole campaign on an unfogged map" onclick={() => app.timelapseRequest++}>
        🎬 watch the campaign timelapse
      </button>
    </div>
  {/if}
  <nav>
    <button class:active={tab === 'colonies'} data-testid="tab-colonies" onclick={() => (tab = 'colonies')}>
      Colonies{#if defaultBuildCount > 0}<span class="idlebadge" data-testid="default-build-badge" title="{defaultBuildCount} colon{defaultBuildCount > 1 ? 'ies are' : 'y is'} not actively constructing (empty queue, housing or trade goods)">{defaultBuildCount}</span>{/if}
    </button>
    <button class:active={tab === 'map'} data-testid="tab-map" onclick={() => (tab = 'map')}>Map</button>
    <button class:active={tab === 'research'} class:pulse={researchIdle} data-testid="tab-research" onclick={() => (tab = 'research')}>Research</button>
    <button class:active={tab === 'fleets'} data-testid="tab-fleets" onclick={() => (tab = 'fleets')}>
      Fleets{#if fleetsInFlight > 0}<span class="idlebadge flying" data-testid="fleets-in-flight-badge" title="{fleetsInFlight} fleet{fleetsInFlight > 1 ? 's' : ''} in flight">{fleetsInFlight}</span>{/if}
    </button>
    <button class:active={tab === 'designer'} data-testid="tab-designer" onclick={() => (tab = 'designer')}>Designer</button>
    <button class:active={tab === 'empires'} data-testid="tab-empires" onclick={() => (tab = 'empires')}>
      Empires{#if seatIssues.length > 0}<span class="idlebadge" data-testid="seat-issue-badge" title="{seatIssues.length} seat{seatIssues.length > 1 ? 's need' : ' needs'} attention (disconnected player or bot stand-in) — details here">⏳{seatIssues.length}</span>{/if}
    </button>
    <button
      class:active={tab === 'reports'}
      data-testid="tab-reports"
      onclick={() => {
        tab = 'reports';
        seenReports = app.reportsSeq;
      }}
    >Reports{app.reportsSeq > seenReports ? ` (${app.reportsSeq - seenReports})` : ''}</button>
    {#if leaderOfferCount > 0 && tab !== 'empires'}
      <button
        class="offers"
        class:quiet={urgentOfferCount === 0}
        data-testid="leader-offer-badge"
        title={urgentOfferCount > 0
          ? 'a leader is waiting for your answer on the Empires tab'
          : 'ignored leader offer(s) — still hireable on the Empires tab; auto-play will not stop for them'}
        onclick={() => (tab = 'empires')}
      >
        {urgentOfferCount > 0 ? '🎖' : '🔕'} {leaderOfferCount} leader offer{leaderOfferCount > 1 ? 's' : ''}
      </button>
    {/if}
    {#if app.replays.some((r) => !r.watched)}
      <button class="replays" data-testid="new-replays" onclick={() => (tab = 'empires')}>
        ⚔ {app.replays.filter((r) => !r.watched).length} new battle{app.replays.filter((r) => !r.watched).length > 1 ? 's' : ''}
      </button>
    {/if}
  </nav>
  {#if battlePhaseInfo && !myBattle}
    <div class="banner dim" data-testid="battle-spectate">Battles in progress elsewhere: {battlePhaseInfo}</div>
  {/if}
  <section>
    {#if tab === 'colonies'}
      <Spreadsheet />
    {:else if tab === 'map'}
      <MapView />
    {:else if tab === 'research'}
      <Research />
    {:else if tab === 'fleets'}
      <Fleets />
    {:else if tab === 'designer'}
      <Designer />
    {:else if tab === 'reports'}
      <Reports />
    {:else}
      <Empires />
    {/if}
  </section>
  {#if celebration}
    <div class="celebration" data-testid="research-celebration" role="status">
      <div class="burst">🎉</div>
      <div>
        <b>Breakthrough: {pretty(celebration.field)}!</b>
        {#if celebration.granted.length}
          <div class="apps">unlocked: {celebration.granted.map(pretty).join(' · ')}</div>
        {/if}
        {#if researchIdle}
          <button class="next" onclick={() => { tab = 'research'; celebration = null; }}>Choose next research →</button>
        {/if}
      </div>
      <button class="x" onclick={() => (celebration = null)}>✕</button>
    </div>
  {/if}
  {#if arrival}
    <div class="celebration arrival" data-testid="colony-ship-arrival" role="status">
      <div class="burst">🚀</div>
      <div>
        <b>Colony ship at {starName(arrival.starId)}</b>
        <div class="apps">a colonizable planet awaits — settle it from the map</div>
        <button class="next" onclick={() => { app.focusStarId = arrival!.starId; tab = 'map'; arrival = null; }}>View on map →</button>
      </div>
      <button class="x" onclick={() => (arrival = null)}>✕</button>
    </div>
  {/if}
  {#if fleetArrivals && !arrival}
    <div class="celebration arrival" data-testid="fleet-arrival" role="status">
      <div class="burst">🛬</div>
      <div>
        <b>Fleet{fleetArrivals.length > 1 ? 's' : ''} arrived</b>
        {#each fleetArrivals as fa (fa.starId)}
          <div class="apps">
            {fa.ships} ship{fa.ships > 1 ? 's' : ''} at {starName(fa.starId)}
            <button class="next" onclick={() => { app.focusStarId = fa.starId; tab = 'map'; fleetArrivals = null; }}>View →</button>
          </div>
        {/each}
      </div>
      <button class="x" onclick={() => (fleetArrivals = null)}>✕</button>
    </div>
  {/if}
  {#if app.contactFlash}
    {@const selfId = session().playerId}
    {@const others = [...new Set(
      app.contactFlash.pairs
        .filter(([x, y]) => x === selfId || y === selfId)
        .map(([x, y]) => (x === selfId ? y : x)),
    )]}
    {@const otherNames = app.contactFlash.pairs
      .filter(([x, y]) => x !== selfId && y !== selfId)
      .map(([x, y]) => `${gs.empires.find((e) => e.id === x)?.name ?? x} ⟷ ${gs.empires.find((e) => e.id === y)?.name ?? y}`)
      .join(' · ')}
    <div class="contact-overlay" data-testid="contact-flash" role="alertdialog">
      <div class="contact-box">
        <div class="contact-title">CONTACT</div>
        {#if others.length}
          <!-- their envoys, procedurally generated like the ships: the same
               empire always sends the same face across the whole game -->
          <div class="envoys">
            {#each others as oid (oid)}
              {@const emp = gs.empires.find((e) => e.id === oid)}
              <div class="envoy" data-testid="envoy-{oid}">
                <DiplomatPortrait seed={`${gs.seed}/${emp?.raceName ?? oid}/${oid}`} colorHex={playerColor(oid)} size={96} />
                <b style:color={playerColor(oid)}>{emp?.name ?? oid}</b>
                <span class="race">{emp?.raceName ?? ''}</span>
              </div>
            {/each}
          </div>
          <p>{others.length > 1 ? 'Their envoys await.' : 'An envoy awaits.'}</p>
        {/if}
        {#if otherNames}
          <p class="contact-sub">also met: {otherNames}</p>
        {/if}
        <p class="contact-sub">
          The empires have met at turn {app.contactFlash.turn}. Fast start is over: everyone now stands at the synced
          turn (turns you previewed past it were rewound) and the game continues turn-by-turn. This is a good moment to
          save.
        </p>
        <div class="contact-actions">
          <button data-testid="contact-save" disabled={!getActive()?.store} onclick={saveGame}>💾 Save the contact turn</button>
          {#if others.length}
            <button data-testid="contact-diplomacy" class="primary" onclick={() => { tab = 'empires'; app.contactFlash = null; }}>🤝 Open diplomacy</button>
          {/if}
          <button data-testid="contact-continue" class="primary" onclick={() => (app.contactFlash = null)}>Continue ▶</button>
        </div>
      </div>
    </div>
  {/if}
  {#if meetQueue.length > 0 && !app.contactFlash}
    {#key meetQueue[0]}
      <FirstContactDialog
        empireId={meetQueue[0]!}
        onclose={() => (meetQueue = meetQueue.slice(1))}
        ondiplomacy={() => {
          tab = 'empires';
          meetQueue = meetQueue.slice(1);
        }}
      />
    {/key}
  {/if}
  {#if starScans.length > 0}
    {#key starScans[0]}
      <StarScanDialog
        starId={starScans[0]!}
        pending={starScans.length - 1}
        onclose={() => (starScans = starScans.slice(1))}
        onviewmap={() => {
          app.focusStarId = starScans[0]!;
          tab = 'map';
          starScans = starScans.slice(1);
        }}
        onskipall={() => (starScans = [])}
      />
    {/key}
  {/if}
  {#if myBattle}
    <!-- keyed so a SECOND battle in the same turn gets a fresh dialog with
         fresh defaults, instead of silently reusing the first one's state -->
    {#key myBattle.id}
      <BattleOrdersDialog battle={myBattle} />
    {/key}
  {/if}
  {#if app.viewing}
    <BattleViewer replay={app.viewing} onclose={() => (app.viewing = null)} />
  {:else if app.viewingGround}
    <!-- the invasion playback waits its turn behind the ship-battle replay
         (the pass is watched first, then the landing it enabled) -->
    {#key `${app.viewingGround.turn}:${app.viewingGround.payload.colonyId}`}
      <GroundBattleDialog
        battle={app.viewingGround}
        onclose={() => {
          if (app.viewingGround) app.viewingGround.watched = true;
          app.viewingGround = null;
        }}
      />
    {/key}
  {/if}
  {#if timelapse}
    <TimelapseViewer data={timelapse} onclose={() => (timelapse = null)} />
  {/if}
  {#if memoryOnly && !memoryNoteDismissed}
    <div class="banner warn" data-testid="memory-only">
      ⚠ Make sure to 💾 save every turn — the browser database is not accessible, so this tab won't survive a reload on its own.
      <button class="x" data-testid="memory-only-dismiss" title="dismiss" onclick={() => (memoryNoteDismissed = true)}>✕</button>
    </div>
  {/if}
  <footer>
    {#if edgeShown === 'red'}
      <div class="allwaiting" data-testid="all-waiting" aria-hidden="true">⏳ Everyone else has committed — the galaxy waits on you!</div>
    {/if}
    <select data-testid="chat-to" bind:value={chatTo} title="everyone or a direct message">
      <option value={-1}>all</option>
      {#each roster.filter((p) => p.id !== session().playerId) as p (p.id)}
        <option value={p.id}>DM {p.name}</option>
      {/each}
    </select>
    <input data-testid="chat-input" bind:value={chatText} placeholder={chatTo === -1 ? 'chat…' : 'direct message…'} onkeydown={(e) => e.key === 'Enter' && sendChat()} />
    <button data-testid="chat-send" onclick={sendChat}>Send</button>
    <span class="chatlog" data-testid="chat-log">
      {#each chatVisible.slice(-3) as m (m.id)}
        <span class:dm={m.to !== -1}>#{m.from}{m.to !== -1 ? '→' + m.to : ''}: {m.text}</span>
      {/each}
    </span>
    <button class="helpbtn" data-testid="help" onclick={() => (showHelp = !showHelp)}>?</button>
    <span class="hash" data-testid="state-hash">{authHash}</span>
  </footer>
  {#if showHelp}
    <div class="help" data-testid="help-panel">
      <h3>Quick reference <button onclick={() => (showHelp = false)}>✕</button></h3>
      <ul>
        <li><b>Colonies</b> — the spreadsheet runs your empire: assign jobs (drag citizen icons onto another job or another colony), pick builds, buy with BC. Click headers to sort; tick rows for bulk builds; 🏛 lists buildings (sell for half price).</li>
        <li><b>Turns</b> are simultaneous: everything resolves when every player commits. Uncommit any time before the last player commits. In <b>⚡ fast start</b> games nobody waits: end turns at your own pace until two empires meet — CONTACT then pulls everyone back to the synced turn (max 10 turns ahead of the slowest player).</li>
        <li><b>Food</b> feeds colonists (2 per unit ×½); shortages starve growth. Freighters move surplus between colonies at 0.5 BC per freighter in use (idle ones are free); without freighter capacity food cannot be shipped at all. Blockades cut deliveries.</li>
        <li><b>Research</b> works one field at a time; basic fields and Cold Fusion (marked ✦) grant <i>all</i> their applications — Cold Fusion delivers colony ships, outposts, transports and freighters together. Never leave research idle — points bank but nothing finishes.</li>
        <li><b>Ships</b> travel star-to-star within fuel range (unreachable stars are dashed red on the map). Move orders can be re-routed until you commit.</li>
        <li><b>Hotkeys</b> — <kbd>1</kbd>–<kbd>7</kbd> switch tabs, <kbd>E</kbd> ends/commits the turn, <kbd>Shift+E</kbd> = ⏩ auto-play until something needs a decision. On the map: select your star, <kbd>B</kbd> arms build mode, then <kbd>C</kbd>olony ship / <kbd>S</kbd>cout / <kbd>F</kbd>rigate / <kbd>D</kbd>estroyer / c<kbd>R</kbd>uiser / <kbd>B</kbd>attleship / <kbd>T</kbd>itan / <kbd>O</kbd>utpost ship / <kbd>H</kbd>ousing / f<kbd>A</kbd>ctory / <kbd>L</kbd>ab / supercomputer <kbd>K</kbd> queue at the best-suited colony (progress bars appear under the map; ✕ hands the yard back to autopilot). With a fleet selected: <kbd>C</kbd> colonize · <kbd>O</kbd> outpost · <kbd>L</kbd>/<kbd>U</kbd> load/unload transports · <kbd>A</kbd> select all · <kbd>⌫</kbd> cycle. In a battle dialog: arrows pick the stance, <kbd>T</kbd> targets, <kbd>B</kbd> toggles bombard, <kbd>Enter</kbd> locks in.</li>
        <li><b>Colonists</b> move between stars on transports: build one, "load" at a colony, fly it, "unload" (Fleets tab). Within a system they move freely — drag citizens onto a sibling colony in the spreadsheet (no ships needed); between systems the freighter run flies your <i>second-best</i> drive (the newest engines go to the warfleet). Colony bases settle other planets in the same system.</li>
        <li><b>Battles</b> only happen between empires at <b>war</b> — declare it on the Empires tab. A battle is a single pass; set stance/targeting/retreat before the clash. Bombardment after a win uses every weapon's strategic power (bombs and missiles hit hardest, beams at half strength; planetary shields block weak hits outright) but can never wipe out a colony's last population unit. Capturing a colony takes an <b>invasion</b>: barracks train 🪖 marines (1 per 5 turns, 4 boarding each transport you build), and a won battle offers the invade order — the defenders' own marines and militia decide whether the landing succeeds, and the assault plays back automatically over the map (rewatch it on the Empires tab, or stage one in the Battle Lab's ground assault preview). Undefended outposts fall to any winning fleet.</li>
        <li><b>☠ stars</b> are guarded by monsters — clear the keeper to colonize. Orion holds the Guardian and the best worlds in the galaxy.</li>
        <li><b>Leaders</b> offer their services on the Empires tab; colony leaders boost one colony, ship officers the whole fleet.</li>
        <li><b>Victory</b>: conquer everyone, win the council vote (⅔ of population), or build the dimensional portal and beat the Andromedans at home.</li>
        <li><b>Play by mail</b> (📬 on the home screen): one player at a time takes the room, plays, commits and "mails in" — progress uploads on every commit and the turn advances when the last player commits. If a friend is online at the same time, you simply join their live game.</li>
      </ul>
    </div>
  {/if}
{:else}
  <p class="loading">waiting for game state…</p>
{/if}

<style>
  /* the top bar is the DEVICE bezel: bright textured gray machine face */
  header {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 0.45rem 1rem;
    background: var(--device-texture), linear-gradient(180deg, var(--device-mid), var(--device-lo));
    border-bottom: 1px solid var(--device-edge);
    /* bezel text follows the DEVICE layer — light bezels need dark text */
    color: var(--device-text);
    position: sticky;
    top: 0;
    flex-wrap: wrap;
    z-index: 10;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14), 0 2px 18px rgba(0, 0, 0, 0.45);
  }
  .title {
    font-weight: 800;
    font-size: 0.95rem;
    text-transform: uppercase;
    color: var(--title-color, var(--accent-soft));
    text-shadow: var(--title-glow, 0 0 16px color-mix(in srgb, var(--accent) 60%, transparent));
    letter-spacing: 0.09em;
    white-space: nowrap;
  }
  .stat {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .stat .lbl {
    color: var(--text-dim);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .delta {
    color: var(--good);
    font-size: 0.85rem;
  }
  .delta.neg,
  .stat.neg {
    color: var(--bad);
  }
  .researching {
    font-size: 0.85rem;
    border-color: var(--line);
  }
  .researching.idle {
    background: linear-gradient(180deg, #5a4a20, #4a3a16);
    border-color: var(--gold);
    color: var(--gold);
    animation: pulse-warn 1.6s ease-in-out infinite;
  }
  .ff {
    font-size: 1rem;
    padding: 0.15rem 0.5rem;
  }
  .ff.active {
    background: linear-gradient(180deg, #2c7a4e, #1d5236);
    border-color: var(--good, var(--good));
    animation: idlepulse 1.6s ease-in-out infinite;
  }
  @keyframes idlepulse {
    0%, 100% { box-shadow: 0 0 0 color-mix(in srgb, var(--good) 0%, transparent); }
    50% { box-shadow: 0 0 10px color-mix(in srgb, var(--good) 50%, transparent); }
  }
  .commit {
    font-weight: 700;
    background: linear-gradient(180deg, #24418a, #1b2f66);
    border-color: #4a6ab8;
    /* the money button glows — blue at rest, gold when it needs you */
    box-shadow: var(--glow);
    /* pinned first in the header: a stable footprint keeps it in the exact
       same place as its label changes (Commit ↔ Committed, +N suffix) */
    min-width: 11.5rem;
    order: -1;
  }
  /* fixed-width turn readout: digits are tabular and the synced detail lives
     in the tooltip, so this stat never resizes its neighbors */
  .turnstat {
    min-width: 6.2rem;
  }
  .syncmark {
    margin-left: 0.15rem;
  }
  .commit.warn {
    background: linear-gradient(180deg, #8a6a1c, #6e5312);
    border-color: var(--gold);
    color: #fff2cf;
    box-shadow: var(--halo-gold);
    animation: pulse-warn 1.6s ease-in-out infinite;
  }
  .commit.committed {
    background: linear-gradient(180deg, #1f6a38, #175028);
    border-color: var(--good);
  }
  @keyframes pulse-warn {
    0%, 100% { box-shadow: 0 0 0 color-mix(in srgb, var(--gold) 0%, transparent); }
    50% { box-shadow: 0 0 14px color-mix(in srgb, var(--gold) 55%, transparent); }
  }
  nav {
    display: flex;
    gap: 0.35rem;
    padding: 0.5rem 1rem 0.2rem;
  }
  nav button {
    border-radius: 8px 8px 0 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    border-color: transparent;
    color: var(--text-dim);
    box-shadow: none;
    text-shadow: none;
  }
  nav button:hover:not(:disabled) {
    box-shadow: none;
    filter: none;
    color: var(--text);
    border-color: transparent;
  }
  /* the active tab is a lit pane of the SCREEN, not a device key */
  nav button.active {
    background: linear-gradient(180deg, var(--panel-3), var(--panel-2));
    color: var(--accent-soft);
    border: 1px solid var(--line-bright);
    border-bottom: 2px solid var(--accent);
    box-shadow: none;
  }
  nav button.pulse:not(.active) {
    color: var(--gold);
    animation: pulse-warn 1.6s ease-in-out infinite;
  }
  nav .replays {
    margin-left: auto;
    background: linear-gradient(180deg, #6e2a2a, #521d1d);
    border: 1px solid #a05050;
    color: #ffd9d0;
  }
  nav .offers {
    margin-left: auto;
    background: linear-gradient(180deg, #6a5424, #54431c);
    border: 1px solid var(--gold);
    color: #ffe9b0;
    animation: pulse-warn 1.6s ease-in-out infinite;
  }
  /* every offer muted: stop shouting, stay findable */
  nav .offers.quiet {
    animation: none;
    opacity: 0.75;
    background: var(--device-texture), linear-gradient(180deg, var(--device-mid), var(--device-lo));
    border-color: var(--device-edge);
    color: var(--device-text);
  }
  nav .offers + .replays {
    margin-left: 0.35rem;
  }
  section {
    padding: 0.6rem 1rem 1.2rem;
    /* fill the leftover viewport height (main is a column flex) so the chat
       footer sits flush at the bottom instead of floating above a gap */
    flex: 1 0 auto;
  }
  footer {
    position: sticky;
    bottom: 0;
    background: var(--device-texture), linear-gradient(0deg, var(--device-lo), var(--device-mid));
    border-top: 1px solid var(--device-edge);
    padding: 0.35rem 1rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    z-index: 10;
  }
  /* urgency wash over the chat bar: translucent + click-through, so the
     layout never shifts and the chat stays usable underneath */
  .allwaiting {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(190, 40, 40, 0.4);
    color: #ffe2e2;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
    pointer-events: none;
    z-index: 3;
  }
  .chatlog {
    display: flex;
    gap: 1rem;
    opacity: 0.8;
    font-size: 0.85rem;
    overflow: hidden;
    white-space: nowrap;
  }
  .hash {
    margin-left: auto;
    opacity: 0.3;
    font-family: monospace;
    font-size: 0.72rem;
  }
  .banner {
    background: linear-gradient(180deg, #2c6a3c, #235430);
    padding: 0.5rem 1rem;
    font-weight: 700;
  }
  .banner.warn {
    background: linear-gradient(180deg, #6a5424, #54431c);
  }
  .banner.dim {
    background: var(--panel-2);
    font-weight: 400;
    color: var(--text-dim);
  }
  .banner.urgent {
    background: linear-gradient(180deg, #7a2c24, #5e211b);
    color: #ffe3dd;
  }
  .tax {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .nohist {
    font-size: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
  }
  .aggro {
    font-size: 0.8rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: #ffb0a6;
  }
  .edge {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 35;
    border-radius: 2px;
  }
  .edge.green {
    box-shadow: inset 0 0 0 3px color-mix(in srgb, var(--good) 55%, transparent), inset 0 0 26px color-mix(in srgb, var(--good) 18%, transparent);
  }
  .edge.red {
    animation: edgepulse 1.2s ease-in-out infinite;
  }
  @keyframes edgepulse {
    0%, 100% { box-shadow: inset 0 0 0 3px color-mix(in srgb, var(--bad) 55%, transparent), inset 0 0 26px color-mix(in srgb, var(--bad) 16%, transparent); }
    50% { box-shadow: inset 0 0 0 5px color-mix(in srgb, var(--bad) 95%, transparent), inset 0 0 44px color-mix(in srgb, var(--bad) 30%, transparent); }
  }
  .dm {
    color: #d7a7ff;
  }
  .helpbtn {
    margin-left: 0.5rem;
  }
  .help {
    position: fixed;
    bottom: 3rem;
    right: 1rem;
    width: 30rem;
    max-height: 65vh;
    overflow-y: auto;
    background: linear-gradient(180deg, var(--panel-2), var(--panel));
    border: 1px solid var(--line-bright);
    border-radius: 10px;
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    z-index: 30;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
  }
  .help h3 {
    display: flex;
    justify-content: space-between;
    margin: 0.2rem 0 0.5rem;
  }
  .help li {
    margin-bottom: 0.35rem;
  }
  .celebration {
    position: fixed;
    top: 4rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.8rem;
    align-items: center;
    background: linear-gradient(135deg, #23408a, #1a2c5e 60%, #182450);
    border: 1px solid var(--accent);
    border-radius: 12px;
    padding: 0.7rem 1.1rem;
    z-index: 45;
    box-shadow: var(--halo), 0 10px 40px rgba(0, 0, 0, 0.6);
    animation: drop-in 0.45s cubic-bezier(0.2, 1.4, 0.4, 1);
  }
  @keyframes drop-in {
    from { transform: translate(-50%, -140%); opacity: 0; }
    to { transform: translate(-50%, 0); opacity: 1; }
  }
  .celebration .burst {
    font-size: 1.8rem;
    animation: spin-pop 0.8s ease;
  }
  @keyframes spin-pop {
    0% { transform: scale(0.2) rotate(-120deg); }
    70% { transform: scale(1.25) rotate(10deg); }
    100% { transform: scale(1) rotate(0); }
  }
  .celebration .apps {
    font-size: 0.85rem;
    color: var(--accent-soft);
    text-transform: capitalize;
  }
  .celebration .next {
    margin-top: 0.3rem;
    background: linear-gradient(180deg, #8a6a1c, #6e5312);
    border-color: var(--gold);
  }
  .celebration .x {
    background: transparent;
    border: none;
    color: var(--text-dim);
  }
  .loading {
    padding: 2rem;
    color: var(--text-dim);
  }
  .commit.fast {
    background: linear-gradient(180deg, #2a6a8a, #1b4a66);
    border-color: #4aa8d8;
  }
  .commit.fast:disabled {
    opacity: 0.6;
  }
  .idlebadge {
    display: inline-block;
    margin-left: 0.3rem;
    min-width: 1.1rem;
    padding: 0 0.25rem;
    border-radius: 999px;
    background: linear-gradient(180deg, #6a5424, #54431c);
    border: 1px solid var(--gold);
    color: #ffe9b0;
    font-size: 0.72rem;
    text-align: center;
  }
  /* fleets-in-flight: informational (blue), not a nag (gold) */
  .idlebadge.flying {
    background: linear-gradient(180deg, #24406a, #1c3254);
    border-color: var(--accent);
    color: #bfe6ff;
  }
  .celebration.arrival {
    border-color: var(--good);
    box-shadow: 0 0 40px color-mix(in srgb, var(--good) 35%, transparent), 0 10px 40px rgba(0, 0, 0, 0.5);
  }
  .contact-overlay {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.82);
    animation: contact-flash 0.9s ease;
  }
  @keyframes contact-flash {
    0% { background: rgba(255, 230, 160, 0.95); }
    30% { background: rgba(160, 40, 40, 0.75); }
    100% { background: rgba(0, 0, 0, 0.82); }
  }
  .contact-box {
    max-width: 34rem;
    background: linear-gradient(180deg, var(--panel-3), var(--panel));
    border: 2px solid var(--gold);
    border-radius: 14px;
    padding: 1.2rem 1.6rem;
    text-align: center;
    box-shadow: 0 0 80px color-mix(in srgb, var(--gold) 35%, transparent);
  }
  .contact-title {
    font-size: 2.4rem;
    font-weight: 900;
    letter-spacing: 0.35em;
    color: var(--gold);
    text-shadow: 0 0 24px color-mix(in srgb, var(--gold) 80%, transparent);
    animation: pulse-warn 1.2s ease-in-out infinite;
  }
  .contact-sub {
    color: var(--text-dim);
    font-size: 0.9rem;
  }
  .envoys {
    display: flex;
    gap: 1.2rem;
    justify-content: center;
    margin: 0.6rem 0 0.2rem;
    flex-wrap: wrap;
  }
  .envoy {
    display: grid;
    justify-items: center;
    gap: 0.15rem;
    animation: envoy-in 0.7s ease-out backwards;
    animation-delay: 0.35s;
  }
  @keyframes envoy-in {
    from { opacity: 0; transform: translateY(8px); filter: brightness(2.5); }
    to { opacity: 1; transform: none; filter: none; }
  }
  .envoy .race {
    font-size: 0.75rem;
    color: var(--text-dim);
  }
  .contact-actions {
    display: flex;
    gap: 0.6rem;
    justify-content: center;
    margin-top: 0.6rem;
  }
  .contact-actions .primary {
    background: linear-gradient(180deg, #24418a, #1b2f66);
    border-color: #4a6ab8;
    font-weight: 700;
  }
</style>
